import { useMemo } from "react";
import { useNavigate } from "react-router-dom";
import { Check, ShoppingCart } from "lucide-react";
import { Icon } from "@/components/site/Icon";
import { SectionHeading } from "@/components/site/ui";
import { useFetch } from "@/lib/useFetch";
import { buyCustom } from "@/lib/cart";
import { inr, track } from "@/lib/api";

export function PackageBuilder({ selected, setSelected }) {
    const nav = useNavigate();
    const { data: services } = useFetch("/services", []);
    const { data: meta } = useFetch("/meta", { gst_rate: 18 });
    const gstRate = meta?.gst_rate || 18;

    const toggle = (slug) => setSelected((prev) => prev.includes(slug) ? prev.filter((s) => s !== slug) : [...prev, slug]);

    const { subtotal, gst, total } = useMemo(() => {
        const sub = (services || []).filter((s) => selected.includes(s.slug)).reduce((a, s) => a + s.starting_price, 0);
        const g = Math.round((sub * gstRate) / 100);
        return { subtotal: sub, gst: g, total: sub + g };
    }, [selected, services, gstRate]);

    const checkout = () => { buyCustom(selected); track("cta_click", { cta: "custom_checkout", count: selected.length }); nav("/checkout"); };

    return (
        <section id="builder" className="bg-teal-800 py-20 grain" data-testid="package-builder">
            <div className="container-x">
                <div className="mb-10 text-center flex flex-col items-center">
                    <SectionHeading center eyebrow="Custom Plan" title="Build Your Own Digital Growth Package"
                        subtitle="Select the services you need and see live pricing including GST." />
                </div>
                <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
                    <div className="lg:col-span-2 grid grid-cols-1 sm:grid-cols-2 gap-4">
                        {(services || []).map((s) => {
                            const on = selected.includes(s.slug);
                            return (
                                <button key={s.slug} onClick={() => toggle(s.slug)} data-testid={`builder-service-${s.slug}`}
                                    className={`text-left rounded-2xl p-5 border transition-all ${on ? "border-gold bg-gold/10 shadow-[0_0_24px_rgba(229,195,120,0.15)]" : "border-brandborder bg-teal-700/40 hover:border-teal-500"}`}>
                                    <div className="flex items-start justify-between">
                                        <div className="w-10 h-10 rounded-lg bg-gold/15 flex items-center justify-center"><Icon name={s.icon} className="w-5 h-5 text-gold" /></div>
                                        <div className={`w-6 h-6 rounded-full border flex items-center justify-center ${on ? "bg-gold border-gold" : "border-cream-soft/40"}`}>
                                            {on && <Check className="w-4 h-4 text-teal-900" />}
                                        </div>
                                    </div>
                                    <h4 className="mt-3 font-bold text-cream">{s.name}</h4>
                                    <div className="mt-1 font-mono font-bold text-gold">{inr(s.starting_price)}</div>
                                </button>
                            );
                        })}
                    </div>
                    <div className="card-teal rounded-2xl p-6 h-fit lg:sticky lg:top-24" data-testid="builder-summary">
                        <h4 className="font-serif text-xl text-cream mb-4">Your Custom Plan</h4>
                        {selected.length === 0 ? (
                            <p className="text-sm text-cream-soft">Select services to see your live price.</p>
                        ) : (
                            <ul className="space-y-2 mb-4">
                                {(services || []).filter((s) => selected.includes(s.slug)).map((s) => (
                                    <li key={s.slug} className="flex justify-between text-sm text-cream-soft"><span>{s.name}</span><span className="font-mono">{inr(s.starting_price)}</span></li>
                                ))}
                            </ul>
                        )}
                        <div className="border-t border-brandborder pt-4 space-y-2 text-sm">
                            <Row label="Subtotal" value={inr(subtotal)} />
                            <Row label={`GST (${gstRate}%)`} value={inr(gst)} />
                            <div className="flex justify-between pt-2 border-t border-brandborder"><span className="font-bold text-cream">Total</span><span className="font-mono font-bold text-xl text-gold" data-testid="builder-total">{inr(total)}</span></div>
                        </div>
                        <button disabled={!selected.length} onClick={checkout} className="btn-gold w-full mt-5 rounded-full py-3 flex items-center justify-center gap-2 disabled:opacity-50 disabled:cursor-not-allowed" data-testid="builder-checkout-btn">
                            <ShoppingCart className="w-4 h-4" /> Continue to Checkout
                        </button>
                    </div>
                </div>
            </div>
        </section>
    );
}

function Row({ label, value }) {
    return <div className="flex justify-between text-cream-soft"><span>{label}</span><span className="font-mono">{value}</span></div>;
}
